/**
 * The end's own answer, kept where nothing can overwrite it.
 *
 * ── WHY THIS EXISTS AT ALL ──────────────────────────────────────────────────
 * "No próximo treino" (+2,5 kg, one set more…) is `renderSummaryChanges` over
 * `changes` / `nextSessionChanges`. Both come from `advancePlan` at the REAL
 * ending and are not stored, so `GET /me/sessions/:id/summary` answers them as
 * empty lists by design — recomputing them would advance the plan a second
 * time. The only copy that ever exists is the answer to
 * `POST /me/sessions/:id/end`, which the outbox sends and which the runner
 * used to throw away. `provider.tsx` hands it here through the runner's
 * `onSent` hook (T31).
 *
 * ── WHY NOT WRITE IT OVER THE SUMMARY'S KEY ────────────────────────────────
 * Because it would not survive. `workoutKeys.summary(id)` is
 * `["workout","session",id,"summary"]`, UNDER `workoutKeys.sessionAll`
 * (`["workout","session"]`), which every session and set write invalidates —
 * including the end itself, invalidated by the runner right after the pass.
 * The refetch would replace the stored answer with the GET's empty lists.
 * `["workout","end-answer",id]` shares only the `"workout"` element, and
 * nothing invalidates the bare `["workout"]` prefix (`options.ts`:
 * `SET_WRITES` and `endSessionMutation().invalidates`). `end-answer.test.ts`
 * is what keeps that true.
 *
 * ── WHAT IS ACCEPTED TO BE LOST ─────────────────────────────────────────────
 * This lives in the Query cache only, and that cache is disposable (D17):
 * an update that changes a cached shape busts it (`buster`, `D190`), and
 * sign-out clears it.
 * A session whose end synced on ANOTHER device, or whose end was replayed
 * after the app was killed (`changed: false`, empty lists), has no answer
 * here and the screen falls back to the GET — records, milestones and
 * "Próximo treino" are still right, only the reasons are missing. No second
 * storage is added for it.
 */
import type { SessionEndOutput, SessionSummary } from "@repo/contract/training";
import { type QueryClient, skipToken, useQuery } from "@tanstack/react-query";

/** Outside every invalidated prefix: nothing refetches it, nothing overwrites it. */
export const endAnswerKey = (sessionId: string) =>
  ["workout", "end-answer", sessionId] as const;

export interface EndAnswer {
  /** `undefined` from a server older than `D144`'s parameterised changes. */
  readonly changes: SessionSummary["changes"];
  readonly nextSessionChanges: SessionSummary["nextSessionChanges"];
}

/**
 * Keep the two lists, and only when this end is the one that ended the
 * session. A replay — the outbox resending after a timeout, or after the app
 * was killed between the server's write and `markDone` — answers
 * `changed: false` with empty lists (`D19`, `endSession`'s header), and
 * storing those would erase a real answer already held.
 */
export function rememberEndAnswer(
  client: Pick<QueryClient, "setQueryData">,
  sessionId: string,
  output: SessionEndOutput
): void {
  if (!output.changed) {
    return;
  }
  client.setQueryData<EndAnswer>(endAnswerKey(sessionId), {
    changes: output.summary.changes,
    nextSessionChanges: output.summary.nextSessionChanges,
  });
}

/**
 * The stored answer, or `undefined` until the end has synced on this device.
 *
 * `skipToken` is what makes this a read of the cache and never a fetch: the
 * query has no function, so it is permanently disabled and only re-renders
 * when `rememberEndAnswer` writes to its key.
 */
export function useEndAnswer(sessionId: string): EndAnswer | undefined {
  const query = useQuery<EndAnswer>({
    queryFn: skipToken,
    queryKey: endAnswerKey(sessionId),
  });
  return query.data;
}

/**
 * The GET's summary with the end's own two lists put back.
 *
 * Everything else stays the server's: records, milestones and
 * `nextPlanSessionId` are recomputed correctly by the GET, and the end's copy
 * of them is no fresher.
 */
export function withEndAnswer(
  summary: SessionSummary,
  answer: EndAnswer | undefined
): SessionSummary {
  if (answer === undefined) {
    return summary;
  }
  return {
    ...summary,
    changes: answer.changes,
    nextSessionChanges: answer.nextSessionChanges,
  };
}
